import React, { useState } from "react";
import { Phone, Mail, MapPin, Send, Instagram } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const initial = { name: "", email: "", phone: "", date: "", guests: "", type: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Por favor, completa nombre, email y mensaje.");
      return;
    }
    setSubmitting(true);

    const subject = `Consulta de ${form.name} — Velero Zorba`;
    const body =
      `Nombre: ${form.name}\n` +
      `Email: ${form.email}\n` +
      `Teléfono: ${form.phone || "-"}\n` +
      `Fecha deseada: ${form.date || "-"}\n` +
      `Nº de pasajeros: ${form.guests || "-"}\n` +
      `Tipo de experiencia: ${form.type || "-"}\n\n` +
      `Mensaje:\n${form.message}`;

    const mailto = `mailto:info@zorba.gal?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;

    setTimeout(() => {
      toast.success("¡Gracias! Te responderemos en breve.");
      setForm(initial);
      setSubmitting(false);
    }, 600);
  };

  return (
    <section id="contacto" className="section-pad bg-background" data-testid="contact-section">
      <div className="container-tight grid md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-5">
          <p className="label-eyebrow text-wood mb-4">Contacto</p>
          <h2 className="font-heading text-4xl md:text-5xl leading-tight text-navy" data-testid="contact-title">
            Consulta disponibilidad para tu salida privada.
          </h2>
          <p className="mt-5 text-navy/70 text-base md:text-lg leading-relaxed">
            Cuéntanos qué tipo de experiencia tienes en mente, cuántas personas sois y en qué fecha te gustaría navegar. Te responderemos de forma cercana para orientarte sobre disponibilidad, formato de salida y condiciones.
          </p>
          <p className="mt-4 text-navy/60 text-sm leading-relaxed">
            Salidas privadas desde Portonovo para hasta 12 pasajeros más tripulación. Media jornada, jornada completa, atardeceres, empresas, rodajes y experiencias a medida.
          </p>

          <div className="mt-10 space-y-6">
            <a
              href="https://wa.me/34649927028?text=Hola%2C%20me%20gustar%C3%ADa%20saber%20m%C3%A1s%20sobre%20el%20velero%20Zorba."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-btn"
              className="flex items-start gap-4 group"
            >
              <span className="w-11 h-11 rounded-full bg-navy text-white flex items-center justify-center flex-shrink-0 group-hover:bg-wood transition-colors">
                <Phone size={18} strokeWidth={1.6} />
              </span>
              <div>
                <p className="label-eyebrow text-navy/50">Escríbenos por WhatsApp</p>
                <p className="font-heading text-xl text-navy mt-1 group-hover:text-wood transition-colors">+34 649 927 028</p>
              </div>
            </a>

            <a
              href="mailto:info@zorba.gal"
              data-testid="contact-email-link"
              className="flex items-start gap-4 group"
            >
              <span className="w-11 h-11 rounded-full bg-navy text-white flex items-center justify-center flex-shrink-0 group-hover:bg-wood transition-colors">
                <Mail size={18} strokeWidth={1.6} />
              </span>
              <div>
                <p className="label-eyebrow text-navy/50">Email</p>
                <p className="font-heading text-xl text-navy mt-1 group-hover:text-wood transition-colors">info@zorba.gal</p>
              </div>
            </a>

            <div className="flex items-start gap-4">
              <span className="w-11 h-11 rounded-full bg-navy text-white flex items-center justify-center flex-shrink-0">
                <MapPin size={18} strokeWidth={1.6} />
              </span>
              <div>
                <p className="label-eyebrow text-navy/50">Zona de navegación</p>
                <p className="font-heading text-xl text-navy mt-1">Portonovo · Rías Baixas</p>
                <p className="text-sm text-navy/60 mt-1">Entorno del Parque Nacional das Illas Atlánticas</p>
              </div>
            </div>

            <a
              href="https://www.instagram.com/zorba_sailingboat"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-instagram-link"
              className="flex items-start gap-4 group"
            >
              <span className="w-11 h-11 rounded-full bg-navy text-white flex items-center justify-center flex-shrink-0 group-hover:bg-wood transition-colors">
                <Instagram size={18} strokeWidth={1.6} />
              </span>
              <div>
                <p className="label-eyebrow text-navy/50">Instagram</p>
                <p className="font-heading text-xl text-navy mt-1 group-hover:text-wood transition-colors">@zorba_sailingboat</p>
                <p className="text-sm text-navy/60 mt-1">Síguenos para ver el día a día a bordo</p>
              </div>
            </a>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          noValidate
          data-testid="contact-form"
          className="md:col-span-7 bg-cream rounded-md p-6 md:p-10 border border-border/60"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="name" className="label-eyebrow text-navy/60">Nombre</Label>
              <Input
                id="name" name="name" value={form.name} onChange={onChange}
                placeholder="Tu nombre" required
                data-testid="form-name-input"
                className="mt-2 bg-white border-border/70 h-12"
              />
            </div>
            <div>
              <Label htmlFor="email" className="label-eyebrow text-navy/60">Email</Label>
              <Input
                id="email" name="email" type="email" value={form.email} onChange={onChange}
                placeholder="tu@email.com" required
                data-testid="form-email-input"
                className="mt-2 bg-white border-border/70 h-12"
              />
            </div>
            <div>
              <Label htmlFor="phone" className="label-eyebrow text-navy/60">Teléfono</Label>
              <Input
                id="phone" name="phone" value={form.phone} onChange={onChange}
                placeholder="+34 ..." 
                data-testid="form-phone-input"
                className="mt-2 bg-white border-border/70 h-12"
              />
            </div>
            <div>
              <Label htmlFor="date" className="label-eyebrow text-navy/60">Fecha deseada</Label>
              <Input
                id="date" name="date" type="date" value={form.date} onChange={onChange}
                data-testid="form-date-input"
                className="mt-2 bg-white border-border/70 h-12"
              />
            </div>
            <div>
              <Label htmlFor="guests" className="label-eyebrow text-navy/60">Nº de pasajeros</Label>
              <select
                id="guests" name="guests" value={form.guests} onChange={onChange}
                data-testid="form-guests-input"
                className="mt-2 w-full bg-white border border-border/70 rounded-md h-12 px-3 text-navy"
              >
                <option value="">Selecciona</option>
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
                  <option key={n} value={n}>{n} {n === 1 ? "pasajero" : "pasajeros"}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="type" className="label-eyebrow text-navy/60">Tipo de experiencia</Label>
              <select
                id="type" name="type" value={form.type} onChange={onChange}
                data-testid="form-type-input"
                className="mt-2 w-full bg-white border border-border/70 rounded-md h-12 px-3 text-navy"
              >
                <option value="">Selecciona una opción</option>
                <option value="Atardecer privado">Atardecer privado (≈ 2 h)</option>
                <option value="Medio día clásico">Medio día clásico (3-4 h)</option>
                <option value="Empresas e incentivos">Empresas e incentivos</option>
                <option value="Rodajes y fotografía">Rodajes y fotografía</option>
                <option value="Otra / a medida">Otra / a medida</option>
              </select>
            </div>
          </div>

          <div className="mt-5">
            <Label htmlFor="message" className="label-eyebrow text-navy/60">Mensaje</Label>
            <Textarea
              id="message" name="message" value={form.message} onChange={onChange}
              placeholder="Cuéntanos qué tipo de salida te gustaría, ocasión, preferencias..."
              required rows={5}
              data-testid="form-message-input"
              className="mt-2 bg-white border-border/70 resize-none"
            />
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-4">
            <Button
              type="submit"
              disabled={submitting}
              data-testid="form-submit-btn"
              className="btn-pill bg-navy hover:bg-[#142949] text-white px-7 h-12 inline-flex items-center gap-2"
            >
              {submitting ? "Enviando..." : "Enviar consulta"}
              <Send size={16} />
            </Button>
            <p className="text-xs text-navy/50">
              Al enviar, abriremos tu cliente de email con la consulta lista.
            </p>
          </div>
        </form>
      </div>
    </section>
  );
}
